import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import ApiInterface from './ApiInterface';
import GetArticleResponse from './CRM/Responses/ArticleResponse';
import CustomRedirectResponse from './CRM/Responses/CustomRedirectResponse';
import GetFaqCategoryResponse from './CRM/Responses/FaqCategoryResponse';
import GetFaqResponse from './CRM/Responses/FaqResponse';
import GetLotteryTicketResponse from './CRM/Responses/LotteryTicketResponse';
import GetManualNotificationResponse from './CRM/Responses/ManualNotificationResponse';
import GetMenuItemResponse from './CRM/Responses/MenuItemResponse';
import GetMenuResponse from './CRM/Responses/MenuResponse';
import GetNewsCategoryResponse from './CRM/Responses/NewsCategoryResponse';
import GetNewsResponse from './CRM/Responses/NewsResponse';
import GetPageSettingResponse from './CRM/Responses/PageSettingResponse';
import GetPaginatedResponse from './CRM/Responses/PaginatedResponse';
import GetSeoTagResponse from './CRM/Responses/SeoTagResponse';
import Article from './Models/Article';
import CustomRedirect from './Models/CustomRedirect';
import Faq from './Models/Faq';
import FaqCategory from './Models/FaqCategory';
import LotteryTicket from './Models/LotteryTicket';
import ManualNotification from './Models/ManualNotification';
import Menu from './Models/Menu';
import MenuItem from './Models/MenuItem';
import ModelInterface from './Models/ModelInterface';
import News from './Models/News';
import NewsCategory from './Models/NewsCategory';
import PageSetting from './Models/PageSetting';
import SeoTag from './Models/SeoTag';
import User from './Models/User';

export type Constructor<T> = new (...rest: any[]) => T;

export default abstract class AbstractApi implements ApiInterface {
    /**
     * e.g. /api/v1/
     */
    protected abstract apiPrefix: string;

    /**
     * SubAPI/Module endpoint, i.e. users or news
     */
    protected abstract endpoint: string;

    protected abstract getAxios(): AxiosInstance;

    public getUri(uri: string): string {
        return this.apiPrefix + this.endpoint + uri;
    }

    public get(uri: string, config?: AxiosRequestConfig): Promise<AxiosResponse> {
        return this.getAxios().get(this.getUri(uri), config);
    }

    public post(uri: string, data?: object, config?: AxiosRequestConfig): Promise<AxiosResponse> {
        return this.getAxios().post(this.getUri(uri), data, config);
    }

    public patch(uri: string, data?: object, config?: AxiosRequestConfig): Promise<AxiosResponse> {
        return this.getAxios().patch(this.getUri(uri), data, config);
    }

    public delete(uri: string, config?: AxiosRequestConfig): Promise<AxiosResponse> {
        return this.getAxios().delete(this.getUri(uri), config);
    }

    public async getModelById<T extends ModelInterface>(modelClass: Constructor<T>, id: number): Promise<T> {
        return AbstractApi.mapDataToModel((await this.get(`/${id}`)).data, modelClass);
    }

    public async storeModel<T extends ModelInterface>(model: T): Promise<T> {
        const modelClass = model.constructor as Constructor<T>;
        return AbstractApi.mapDataToModel((await this.post('', model)).data, modelClass);
    }

    public async updateModel<T extends ModelInterface>(model: T): Promise<T> {
        const modelClass = model.constructor as Constructor<T>;
        return AbstractApi.mapDataToModel((await this.patch(`/${model.id}`, model)).data, modelClass);
    }

    public put(uri: string, data?: object, config?: AxiosRequestConfig): Promise<AxiosResponse> {
        return this.getAxios().put(this.getUri(uri), data, config);
    }

    protected static mapPaginatedDataToModel<T extends ModelInterface>(
        paginatedResponse: GetPaginatedResponse,
        Model: new (...rest: any[]) => T
    ): GetPaginatedResponse<T> {
        // paginatedResponse.data is an array of models fetched from API
        paginatedResponse.data = paginatedResponse.data.map((data) => AbstractApi.mapDataToModel(data, Model));

        return paginatedResponse as GetPaginatedResponse<T>;
    }

    public static mapDataToModel<T extends ModelInterface>(data: object, Model: Constructor<T>): T {
        return new Model(data);
    }

    public static mapDataToUserClass(data: object): User {
        return AbstractApi.mapDataToModel(data, User);
    }

    public static mapDataToFaqClass(faq: GetFaqResponse): Faq {
        return AbstractApi.mapDataToModel(faq, Faq);
    }

    public static mapDataToFaqCategoryClass(faqCategory: GetFaqCategoryResponse): FaqCategory {
        return AbstractApi.mapDataToModel(faqCategory, FaqCategory);
    }
    public static mapDataToNewsCategoryModel(newsCategory: GetNewsCategoryResponse): NewsCategory {
        return AbstractApi.mapDataToModel(newsCategory, NewsCategory);
    }

    public static mapDataToMenuClass(menu: GetMenuResponse): Menu {
        return AbstractApi.mapDataToModel(menu, Menu);
    }

    public static mapDataToPageSettingClass(pageSetting: GetPageSettingResponse): PageSetting {
        return AbstractApi.mapDataToModel(pageSetting, PageSetting);
    }

    public static mapDataToMenuItemClass(menuItem: GetMenuItemResponse): MenuItem {
        return AbstractApi.mapDataToModel(menuItem, MenuItem);
    }

    public static mapDataToTicketClass(ticket: GetLotteryTicketResponse): LotteryTicket {
        return AbstractApi.mapDataToModel(ticket, LotteryTicket);
    }

    public static mapDataToArticleClass(menuItem: GetArticleResponse): Article {
        return AbstractApi.mapDataToModel(menuItem, Article);
    }

    public static mapDataToNewsClass(news: GetNewsResponse): News {
        return AbstractApi.mapDataToModel(news, News);
    }

    public static mapDataToManualNotificationClass(menuItem: GetManualNotificationResponse): ManualNotification {
        return AbstractApi.mapDataToModel(menuItem, ManualNotification);
    }

    public static mapDataToCustomRedirectClass(customRedirect: CustomRedirectResponse): CustomRedirect {
        return AbstractApi.mapDataToModel(customRedirect, CustomRedirect);
    }

    public static mapDataToSeoTagClass(seoTag: GetSeoTagResponse): SeoTag {
        return AbstractApi.mapDataToModel(seoTag, SeoTag);
    }
}
